import type { IEvent } from "@/core/plugins/Events/Event";
import type { AxiosInstance } from "axios";
import axios from "axios";
import { injectable } from "inversify";
import "reflect-metadata";
import type { IHttpTokenInterceptor } from "./middleware/HttpTokenInterceptor";
import {
  EHttpEvent,
  HttpClient,
  IHttpDeleteParams,
  IHttpGetParams,
  IHttpPatchParams,
  IHttpPostParams,
  IHttpPutParams,
  IHttpResponse
} from "./protocols/types";
import { financasBaseUrl } from "./protocols";

@injectable()
export class AxiosAdapter implements HttpClient {
  private axios!: AxiosInstance;

  private event: IEvent;

  private axiosTokenInterceptor?: IHttpTokenInterceptor<AxiosInstance>;

  constructor(
    event: IEvent,
    axiosTokenInterceptor?: IHttpTokenInterceptor<AxiosInstance>
  ) {
    this.event = event;
    this.axiosTokenInterceptor = axiosTokenInterceptor;
  }

  setup(url: string) {
    this.axios = axios.create({
      baseURL: url
    });

    this.handleToken();
    this.handleUnauthorized();
  }

  handleToken() {
    this.axiosTokenInterceptor?.intercept(this.axios);
  }

  //@arao
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private handleFeedbackUnauthorized(error: any) {
    const isFinancas = error.config.baseURL === financasBaseUrl;

    if (isFinancas) return;

    this.event?.dispatchEvent(EHttpEvent.unauthorized);
  }

  private handleUnauthorized() {
    this.axios.interceptors.response.use(
      function (response) {
        return response;
      },
      (error) => {
        const isUnauthorized = error.response?.status === 401;

        if (isUnauthorized) this.handleFeedbackUnauthorized(error);

        return Promise.reject(error);
      }
    );

    return axios;
  }

  async patch<T>(params: IHttpPatchParams): Promise<IHttpResponse<T>> {
    const request = await this.axios.patch(
      params.url,
      {
        ...params.body
      },
      params.config
    );

    return {
      statusCode: request.status,
      body: request.data
    };
  }

  async post<T>(params: IHttpPostParams): Promise<IHttpResponse<T>> {
    //const body = Array.isArray(params.body) ? params.body : { ...params.body };

    const request = await this.axios.post(
      params.url,
      params.body,
      params.config
    );

    return {
      statusCode: request.status,
      body: request.data
    };
  }

  async get<T>(params: IHttpGetParams): Promise<IHttpResponse<T>> {
    const request = await this.axios.get(params.url, {
      params: params.queryParams,
      ...params.config
    });

    return {
      statusCode: request.status,
      body: request.data,
      headers: request.headers
    };
  }

  async delete<T>(params: IHttpDeleteParams): Promise<IHttpResponse<T>> {
    const request = await this.axios.delete(params.url, {
      data: params.body,
      params: params.params
    });

    return {
      statusCode: request.status,
      body: request.data
    };
  }

  async put<T>(params: IHttpPutParams): Promise<IHttpResponse<T>> {
    const request = await this.axios.put(params.url, {
      ...params.body
    });

    return {
      statusCode: request.status,
      body: request.data
    };
  }
}
